import React from 'react'
import { Alert, Button, Table } from '@surecompanies/core_components'
import { Empty } from 'antd'
import { SortDirection } from '../../api/dto/user-invitation.dto'
import { PageContent, TableCard } from './UserManagement.styles'
import { ExportModal } from '../ClientBillingProfileManagement/ExportModal'
import { BulkActionBar } from './BulkActionBar'
import { GrantAccessModal } from './GrantAccessModal'
import { ROSTER_EXPORT_SCOPES } from './rosterExport'
import { RemoveAccessModal } from './RemoveAccessModal'
import { ShowingLine } from './ShowingLine'
import { UserHistoryDrawer } from './UserHistoryDrawer'
import { UsersToolbar } from './UsersToolbar'
import { useUsersColumns } from './useUsersColumns'
import { useUsersTable } from './useUsersTable'
import { useUsersTableInteractions } from './useUsersTableInteractions'

const UsersTable = () => {
  const {
    search,
    setSearch,
    page,
    setPage,
    records,
    total,
    stats,
    isFetching,
    isError,
    customerOptions,
    areCompaniesLoading,
    statusFilters,
    scope,
    clientPublicKeys,
    hasHrAdminAccess,
    hasEmployeeAccess,
    sortBy,
    sortDir,
    historyUser,
    pageSize,
    pageSizeOptions,
    handlePageSizeChange,
    handleOpenHistory,
    handleCloseHistory,
    handleStatusFilterChange,
    handleScopeChange,
    handleCompaniesChange,
    handleHrAdminAccessChange,
    handleEmployeeAccessChange,
    handleSortChange,
    hasNarrowedList,
    handleClearFilters,
    selectedEmails,
    selectedRows,
    selectionBuckets,
    selectionRemovalPlan,
    isSelectingAll,
    handleSelectionChange,
    handleClearSelection,
    handleSelectAllFiltered,
    handleReviewNoCompany,
    handleShowAllUsers,
    rosterExport,
    grantBuckets,
    grantModalOpen,
    grantResults,
    grantProgress,
    grantAccessMutation,
    handleOpenBulkGrantAccess,
    handleCloseGrantAccess,
    handleSubmitGrantAccess,
    handleRetryFailedGrants,
    removalPlan,
    removeModalOpen,
    removeResults,
    removeProgress,
    isRemovingAccess,
    handleOpenBulkRemoveAccess,
    handleCloseRemoveAccess,
    handleSubmitRemoveAccess,
  } = useUsersTable()

  const columns = useUsersColumns({
    sortBy,
    sortDir: sortDir as SortDirection | null,
    customerOptions,
    areCompaniesLoading,
    clientPublicKeys,
    hasHrAdminAccess,
    hasEmployeeAccess,
    statusFilters,
    stats,
    onCompaniesChange: handleCompaniesChange,
    onHrAdminAccessChange: handleHrAdminAccessChange,
    onEmployeeAccessChange: handleEmployeeAccessChange,
    onStatusFilterChange: handleStatusFilterChange,
  })

  const { rowSelection, handleRow, handleTableChange } =
    useUsersTableInteractions({
      total,
      selectedEmails,
      onSelectionChange: handleSelectionChange,
      onSelectAllFiltered: handleSelectAllFiltered,
      onClearSelection: handleClearSelection,
      onSortChange: handleSortChange,
      onOpenHistory: handleOpenHistory,
    })

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        hasNarrowedList ? 'No users match these filters' : 'No users yet'
      }
    >
      {hasNarrowedList ? (
        <Button size="small" onClick={handleClearFilters}>
          Clear filters
        </Button>
      ) : null}
    </Empty>
  )

  return (
    <PageContent $hasFloatingBar={selectedEmails.length > 0}>
      {isError && (
        <Alert
          type="error"
          message="Failed to load users. Please refresh the page."
          showIcon
        />
      )}

      <UsersToolbar
        search={search}
        scope={scope}
        stats={stats}
        isFunnelActive={statusFilters.length > 0}
        isExportDisabled={total === 0}
        onSearchChange={setSearch}
        onScopeChange={handleScopeChange}
        onExport={rosterExport.handleOpen}
      />

      <ShowingLine
        page={page}
        pageSize={pageSize}
        total={total}
        noCompanyCount={stats?.noCompanies ?? 0}
        statusFilters={statusFilters}
        onReviewNoCompany={handleReviewNoCompany}
        onShowAllUsers={handleShowAllUsers}
      />

      {selectedEmails.length > 0 ? (
        <BulkActionBar
          selectedCount={selectedEmails.length}
          selectedRows={selectedRows}
          grantBuckets={selectionBuckets}
          removalPlan={selectionRemovalPlan}
          onClear={handleClearSelection}
          onGrantAccess={handleOpenBulkGrantAccess}
          onRemoveAccess={handleOpenBulkRemoveAccess}
        />
      ) : null}

      <TableCard>
        <Table
          dataSource={records}
          columns={columns}
          rowKey="email"
          rowSelection={rowSelection}
          onRow={handleRow}
          // Selecting the whole filter fetches it, and the selection column is
          // about to change, so the table reads as busy either way.
          loading={isFetching || isSelectingAll}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              if (nextPageSize !== pageSize) {
                handlePageSizeChange(nextPageSize)
                return
              }
              setPage(nextPage)
            },
            showSizeChanger: true,
            pageSizeOptions,
          }}
          size="small"
          // One empty state, and the filters that emptied the table can be
          // cleared from it.
          locale={{ emptyText: emptyState }}
          // The column widths plus the selection checkbox. Keep it in step
          // with them, or the frozen Email column overlaps its neighbour.
          scroll={{ x: 1860 }}
        />
      </TableCard>

      <GrantAccessModal
        open={grantModalOpen}
        buckets={grantBuckets}
        confirmLoading={grantAccessMutation.isPending}
        results={grantResults}
        progress={grantProgress}
        onClose={handleCloseGrantAccess}
        onSubmit={handleSubmitGrantAccess}
        onRetryFailed={handleRetryFailedGrants}
      />

      <RemoveAccessModal
        open={removeModalOpen}
        plan={removalPlan}
        confirmLoading={isRemovingAccess}
        results={removeResults}
        progress={removeProgress}
        onClose={handleCloseRemoveAccess}
        onSubmit={handleSubmitRemoveAccess}
      />

      <ExportModal
        open={rosterExport.modalOpen}
        isExporting={rosterExport.isExporting}
        scopes={ROSTER_EXPORT_SCOPES}
        onClose={rosterExport.handleClose}
        onDownload={rosterExport.handleDownload}
      />

      <UserHistoryDrawer user={historyUser} onClose={handleCloseHistory} />
    </PageContent>
  )
}

export { UsersTable }
